import * as React from 'react';
import "./AcceptabilityDashboard.css";
import { connect } from 'react-redux';
import { Dispatch } from "redux";
import * as Actions from '../actions';
import { AnalyseState, WaveformType } from '../interfaces/state';
import { CoughAnalysisSetComment, CoughEventTypeEnum, OverreadStatusEnum, OverreadStatusIdEnum } from '../../../misc/api/apiTypes';
import { AuditModal } from "../../../auditmodal/components/AuditModal";
import once from 'lodash-es/once';
import { useWebPortalLegacyClient, useWebPortalApiClient } from '../../../misc/api/WebPortalTokenClient';
import { useLocation, useNavigate } from 'react-router-dom';
import { OnIgnoreEnter } from '../AnalyseApp';
import { CoughAnalysisSetTypeEnum } from '../../../misc/api/CoughAnalysisSetTypes';
import { CoughAnalysisSet, StudyCoughEventType } from 'webportalclient';
import { useQuery } from "../../../Components/Query";
import { SendForVisualReviewModalBody } from "../../../cough-analysis/SendForVisualReviewModal/SendForVisualReviewModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import { HasPermission } from '../../../misc/components/Authorization';
import Modal from "../../../misc/components/modal";
import { PermissionEnum } from '../../../misc/api/ApiPermissionEnum';
import { useSecurityContext } from '../../../Core/SecurityContext';
import { useCurrentStudyConfigurationContext } from '../../../Core/CurrentStudyConfigurationContext';
import Condition from "../../../misc/components/Condition";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { PeaksInstance } from "peaks.js";
import { AuditModalBody } from '../../../auditmodal/components/AuditModalBody';
import Select from 'react-select';
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";

interface AcceptabilityDashboardProps {
    isVisible: boolean;
    primaryAnalysisSetId: string;
    acceptabilityModalVisiblility: boolean;
    selectedAcceptability: OverreadStatusEnum;
    isSubmitting: boolean;
    coughAnalysisSet: CoughAnalysisSet;
    requiresVisualReview: boolean;
    comments: CoughAnalysisSetComment[];
    dispatch?: Dispatch<AnalyseState>;
    errorStatus?: string | undefined;
    blindComments: boolean;
    eventDuplicateThreshold: number;
    availableEventTypes: StudyCoughEventType[];
    rawTimepoints: Map<string, number>;
    selectedSegment: number;
    selectedWaveform: string;
    peaksJs: PeaksInstance
}

export const AcceptabilityDashboard = (props: AcceptabilityDashboardProps) => {
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const [sendForVisualReviewVisible, setSendForVisualReviewVisible] = React.useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { refresh } = useQuery();
    const { currentUserPermissions } = useSecurityContext();
    const { study, coughSettings } = useCurrentStudyConfigurationContext();
    const [warnRequested, setWarnRequested] = React.useState(false);
    const [requestedAction, setRequestedAction] = React.useState<OverreadStatusEnum>(OverreadStatusEnum.NotStarted);

    let useStatusReason = false;

    React.useEffect(() => {
        if (props.coughAnalysisSet) {
            props.dispatch(
                Actions.InitializeCurrentAnalysisSetAsyncAction.create(
                    client,
                    props.coughAnalysisSet.id
                ))
        }
    }, []);

    const onSubmit = once((audit) => {
        (async () => {

            if (props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.Adjudication) {
                const analysisSetEventsResult = await client.client.getCoughAnalysisSetEvents(
                    undefined,
                    `analysisSetId eq ${props.coughAnalysisSet.id} and disputed eq true and disputedAccepted eq false`
                );

                const setA = analysisSetEventsResult.value ?? [];

                for (const cas of setA) {
                    await client.client.deleteCoughAnalysisSetEvent(cas.id);
                }
            }

            props.dispatch(
                Actions.AcceptabilitySetAsyncAction.create(
                    legacyClient,
                    client.client,
                    location,
                    navigate,
                    props.primaryAnalysisSetId,
                    props.selectedAcceptability,
                    audit.Annotation,
                    useStatusReason
                )
            );

        })();
    });

    const onInitialSubmit = async (status: OverreadStatusEnum) => {
        props.dispatch(Actions.AcceptabilitySetIsSubmittingAction.create(true));
        props.dispatch(Actions.SetAcceptanceErrorAction.create(null));
        props.dispatch(Actions.SetDisputedAcceptanceErrorAction.create(null));
        props.dispatch(Actions.GetPointsForFinalDecision.create(legacyClient))
        .then(points => {
            const coughPeakEvent = props.availableEventTypes.find(x => x.refEventTypeId === CoughEventTypeEnum.CoughPeak);

            const valuesInThreshold = points.filter(x => x.layerPosition === 0 && x.eventTypeId === coughPeakEvent.id)
                .filter((ev1, _, entries) =>
                    entries.some((ev2) => ev1.id !== ev2.id && Math.abs((props.rawTimepoints.get(ev1.id) * 1000) - (props.rawTimepoints.get(ev2.id) * 1000)) <= props.eventDuplicateThreshold)
            );

            const remainingUnconfirmed = valuesInThreshold.filter(x => !x.confirmedValidDuplicate);

            if (props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.Adjudication) {
                
                const remainingUnresolvedDisputedPointsLength = points.filter(x => x.disputed === true && x.disputedAccepted === undefined).length;

                if (remainingUnresolvedDisputedPointsLength > 0) {
                    window.scrollTo(0, 0);
                    props.dispatch(Actions.SetDisputedAcceptanceErrorAction.create(`${remainingUnresolvedDisputedPointsLength} events are marked as disputed and need to be resolved, please review before continuing`));
                    props.dispatch(Actions.AcceptabilitySetIsSubmittingAction.create(false));
                    return;
                }
            }

            if (remainingUnconfirmed.length > 0) {
                window.scrollTo(0, 0);
                const thresholdInSeconds = props.eventDuplicateThreshold / 1000;
                props.dispatch(Actions.SetAcceptanceErrorAction.create(`${remainingUnconfirmed.length} events are within the duplicate threshold of ${thresholdInSeconds}s please review before continuing`));
                props.dispatch(Actions.AcceptabilitySetIsSubmittingAction.create(false));
                return;
            }

            props.dispatch(Actions.AcceptabilitySetIsSubmittingAction.create(false));
            props.dispatch(Actions.ShowAcceptabilityAuditModalAction.create(status));
        });
    }

    const handleSendAnalysisSetForVisualReview = (analysisSetId: string) => {
        setSendForVisualReviewVisible(true);

    }

    const isCorrectTypeForVisualReview = () => props.coughAnalysisSet && !props.coughAnalysisSet?.isLinkedAnalysisSet && (props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.Analysis || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.QualityCheck || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.ProjectQualityAssurance || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.MonitoringQualityAssurance || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.DifficultCougherQualityAssurance || props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.WeeklyQualityAssurance);

    const canSendForVisualReview = (): boolean => {
        return isCorrectTypeForVisualReview() && (props.coughAnalysisSet.status === OverreadStatusIdEnum.Pending);
    }

    const isVisualReviewRequiredOrComplete = () => {
        if (!props.coughAnalysisSet) return false;

        const { requiresVisualReview, coughAnalysisSet } = props;

        if (coughAnalysisSet.visualReviewAnalystSelectionId === null && coughAnalysisSet.firstComplete !== null) {
            return false;
        }

        if (coughAnalysisSet.visualReviewAnalystSelectionId !== null) {

            switch (coughAnalysisSet.visualReviewAnalystSelectionId) {
                case 0:
                    return false;
                case 1:
                    return false;
                case 2:
                    return true;
                case undefined:
                    return true;
            }
        } else if (requiresVisualReview === true) {
            return true;
        } else {
            return false;
        }

    };

    const performAction = (action: OverreadStatusEnum) => {
        if (action == OverreadStatusEnum.Accepted) {
            onInitialSubmit(OverreadStatusEnum.Accepted);
        }
        else if (action == OverreadStatusEnum.Pending) {
            props.dispatch(Actions.AcceptabilitySetAsyncAction.create(legacyClient, client.client, location, navigate, props.primaryAnalysisSetId, OverreadStatusEnum.Pending));
        }
        else if (action == OverreadStatusEnum.Rejected) {
            onInitialSubmit(OverreadStatusEnum.Rejected);
        }
    }

    const stallAction = (action: OverreadStatusEnum) => {
        setRequestedAction(action);

        const totalEvents = props.peaksJs.points.filter({layerPosition: 0}).length + props.peaksJs.segments.filter({layerPosition: 0}).length;

        if (props.coughAnalysisSet.type !== CoughAnalysisSetTypeEnum.QualityCheck || totalEvents != 0) {
            performAction(action);
            return;
        }

        setWarnRequested(true);
    };

    function ActionButtons() {
        return <>
            <div className="tooltip-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
                <button className="btn btn-success" disabled={props.isSubmitting || isVisualReviewRequiredOrComplete()} onClick={() => stallAction(OverreadStatusEnum.Accepted)} onKeyDown={OnIgnoreEnter}>
                    <FontAwesomeIcon aria-hidden="true" icon={faCheck} />&nbsp;Accept
                </button>
                {(props.isSubmitting || isVisualReviewRequiredOrComplete()) && (
                    <span className="tooltip-text">Complete Review before accepting</span>
                )}
            </div>&nbsp;
            <button className="btn btn-warning" disabled={props.isSubmitting} onClick={() => performAction(OverreadStatusEnum.Pending)} onKeyDown={OnIgnoreEnter}>
                <FontAwesomeIcon aria-hidden="true" icon={faCircle as IconProp} />&nbsp;Pending
            </button>&nbsp;
            <Condition test={() => props.coughAnalysisSet.type !== CoughAnalysisSetTypeEnum.Analysis
                || (props.coughAnalysisSet.type === CoughAnalysisSetTypeEnum.Analysis && HasPermission(currentUserPermissions, study.id, PermissionEnum.RejectACoughAnalysisSet))}
            >
                <button className="btn btn-danger" disabled={props.isSubmitting} onClick={() => stallAction(OverreadStatusEnum.Rejected)} onKeyDown={OnIgnoreEnter}>
                    <FontAwesomeIcon aria-hidden="true" icon={faXmark} />&nbsp;Reject
                </button>
            </Condition>
            &nbsp;
            {canSendForVisualReview() ?
                <button className="btn btn-primary" disabled={props.isSubmitting} onClick={() => { handleSendAnalysisSetForVisualReview(props.primaryAnalysisSetId); }} onKeyDown={OnIgnoreEnter}>
                    {props.coughAnalysisSet.visualReviewAnalystSelectionId === 0 || props.coughAnalysisSet.visualReviewAnalystSelectionId === 1 ? "View/Add Review Comments" : "Send For Review"}
                </button> : null}
        </>;
    }

    function WarningMessage() {
        let actionName;
        if(requestedAction === OverreadStatusEnum.Accepted) {
            actionName = "Accept";
        }
        else if(requestedAction === OverreadStatusEnum.Rejected) {
            actionName = "Reject";
        }

        return(
            <div className='row'>
                <div className='col-lg-8'>
                    <FontAwesomeIcon icon={faTriangleExclamation} />&nbsp;
                    You're about to <b>{actionName}</b> the analysis set without marking any events&nbsp;
                </div>
                <div className='col-lg-4 text-right'>
                    <button type="button" className="btn btn-warning" onClick={() => performAction(requestedAction)}>
                        Proceed
                    </button>&nbsp;
                    <button type="button" className="btn btn-info" onClick={() => setWarnRequested(false)}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    const getAuditReasons = (action: OverreadStatusEnum) => {
        if (props.coughAnalysisSet.type !== CoughAnalysisSetTypeEnum.QualityCheck) {
            return [];
        }

        if (action == OverreadStatusEnum.Accepted) {
            const statusAccept = props.selectedWaveform === WaveformType.CWFT_RAW && coughSettings.CoughQcRawAcceptenceReason;

            return !statusAccept? []: [
                "Chest Sensor Low Volume",
                "Chest Sensor High Volume",
                "Chest Sensor Environmental",
                "Chest Sensor Detachment",
                "Chest Sensor Cutting Out",
                "Chest Sensor Issue",
                "Other"
            ];
        }
        else if (action == OverreadStatusEnum.Rejected) {
            const statusReject = coughSettings.CoughQcRejectionReason;

            return !statusReject? []: [
                "Minimum Recording Duration Not Met",
                "Training File",
                "Device Never Attached",
                "Recording Quality Issue",
                "Sponsors Request",
                "Early Termination",
                "Other"
            ];
        }

        return [];
    }

    const AuditDialog = () => {
        if (!props.acceptabilityModalVisiblility) {
            return null;
        }

        const [selectedReason, setSelectedReason] = React.useState('');
        const [errors, setErrors] = React.useState<string[]>([]);
        const [showSelectError, setShowSelectError] = React.useState(false);

        const onClose = () => props.dispatch(Actions.CloseAcceptabilityAuditModalAction.create());
        const reasons = getAuditReasons(requestedAction).map(e => ({value: e, label: e}));

        if (reasons.length > 0) {
            useStatusReason = true;

            return (
                <Modal show={true} closeModalHandler={onClose} header="Analysis Approval">
                    <Alert show={errors.length > 0} category={AlertCategoryEnum.Danger}>
                        <ul>
                            {errors.map((err, index) => (
                                <li key={index}>{err}</li>
                            ))}
                        </ul>
                    </Alert>
                    <AuditModalBody
                      isAuditAnnotationDisabled={selectedReason !== "Other"}
                      annotationLabel="Reason Text"
                      isSubmitting={props.isSubmitting}
                      onClose={onClose}
                      onSubmit={auditForm => {
                        setShowSelectError(false);

                        if (selectedReason == '') {
                            setShowSelectError(true);
                            setErrors(['Please select a reason']);
                            return;
                        }

                        if (selectedReason == "Other") {
                            auditForm.Annotation = auditForm.Annotation.trim();
                            if (auditForm.Annotation == '') {
                                setErrors(['Please specify a reason']);
                                return;
                            } else if (auditForm.Annotation.length > 100) {
                                setErrors(['Input reason length should not exceed 100 character.']);
                                return;
                            }
                        }

                        if (selectedReason != "Other") {
                            auditForm.Annotation = selectedReason;
                        }

                        onSubmit(auditForm);
                      }}
                    >
                        <label>Select Reason</label>
                        <Select
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: showSelectError? 'red': ''
                                })
                            }}
                            options={reasons}
                            value={reasons.find(o => o.value === selectedReason)}
                            onChange={
                                selectedItem => {
                                    setSelectedReason(selectedItem.value);
                                    setShowSelectError(false);
                                    setErrors([]);
                                }
                            }
                        />
                        <br />
                    </AuditModalBody>
                </Modal>
            );
        }

        return (
            <AuditModal
                header="Analysis Approval"
                isSubmitting={props.isSubmitting}
                onSubmit={onSubmit}
                onClose={onClose}
            />
        );
    }

    if (props.isVisible && props.coughAnalysisSet) {
        return (
            <>
                <div className="panel panel-default">
                    <div className={`panel-body ${warnRequested? 'bg-warning text-warning': ''}`}>
                        {!warnRequested? <ActionButtons />: <WarningMessage />}
                    </div>
                </div>
                <AuditDialog />
                {sendForVisualReviewVisible ?
                    <SendForVisualReviewModalBody
                        dispatch={props.dispatch}
                        comments={props.comments}
                        blindComments={props.blindComments}
                        coughAnalysisSet={props.coughAnalysisSet}
                        analysisSetId={props.coughAnalysisSet.id}
                        errorStatus={props.errorStatus}
                        selectedSegment={props.selectedSegment}
                        onClose={() => { setSendForVisualReviewVisible(false); refresh(); }} /> : null}
            </>
        );
    } else {
        return null;
    }
}

function mapStateToProps(state: AnalyseState): AcceptabilityDashboardProps {
    return {
        isVisible: !state.core.isViewOnly,
        primaryAnalysisSetId: state.analysisSet.primaryAnalystSetId,
        acceptabilityModalVisiblility: state.core.isAcceptabilityAuditModalVisible,
        selectedAcceptability: state.core.acceptabilityStatus,
        isSubmitting: state.core.isSubmitting,
        coughAnalysisSet: state.core.coughAnalysisSet,
        requiresVisualReview: state.core.requiresVisualReview,
        comments: state.core.comments,
        blindComments: state.core.blindComments,
        eventDuplicateThreshold: state.core.eventDuplicateThreshold,
        availableEventTypes: state.core.availableEventTypes,
        rawTimepoints: state.core.rawTimestampPoints,
        selectedSegment: state.core.selectedSegment,
        selectedWaveform: state.core.selectedWaveform,
        errorStatus: state.core.visualInspectionErrorMessage,
        peaksJs: state.core.peaksJs
    } as AcceptabilityDashboardProps;
}

export default connect<AcceptabilityDashboardProps, any>(mapStateToProps)(AcceptabilityDashboard);
